import TicketCategoriesTable from './TicketCategoriesTable';
import React, { useEffect, useState, useMemo } from 'react';
import { Alert, Box, Button, LinearProgress, Stack, Typography } from '@mui/material';
import MspAnalyticsApi, { RecentTicketAiSummary, TicketAutomationSummary, AutomationBand } from '../apis/mspAnalyticsApi';
import { getApiErrorMessage } from '../utils/apiErrorMessage';

export default function RecentTicketAiAnalysisPanel({ selectedCategory, days, initial, generation, loading = false, customerIds = [], queueIds = [], onRetry, onDrill, automation, onAutomationDrill }: {
  selectedCategory?: string; automation?: TicketAutomationSummary; onAutomationDrill?: (band:AutomationBand,category:string,customerIds:string[])=>void;
  days: number; initial?: RecentTicketAiSummary; generation?: string | null; loading?: boolean; customerIds?: string[]; queueIds?: string[]; onRetry?: () => void;
  onDrill: (by: string, key: string, label: string, customerIds?: string[]) => void;
}) {
  const [data, setData] = useState(initial);
  const [error, setError] = useState<string | null>(null);
  const [retryLoading, setRetryLoading] = useState(false);
  useEffect(() => {
    if (initial && !(loading && !generation)) setData(initial);
  }, [initial, generation, loading]);
  const retryFailed = async () => {
    setRetryLoading(true);
    try { await MspAnalyticsApi.analyzeRecentTickets(days, true); onRetry?.(); }
    catch (err) { setError(getApiErrorMessage(err, 'Unable to retry categories.')); }
    finally { setRetryLoading(false); }
  };
  const automationCounts=useMemo(()=>{
    const counts:Record<string,Partial<Record<AutomationBand,number>>>={};
    for(const ticket of Object.values(automation?.tickets || {})) {
      if(queueIds.length&&!queueIds.includes(ticket.queue_id || 'unknown'))continue;
      if(customerIds.length&&!customerIds.includes(ticket.company_id || 'unknown'))continue;
      for(const category of ticket.category_keys || []){const bands=counts[category] ||= {};bands[ticket.band]=(bands[ticket.band] || 0)+1;}
    }
    return counts;
  },[automation,customerIds,queueIds]);
  const pending = !!data?.enabled && !!data.pending;
  return <Box sx={{ my: 3 }}>
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
      <Typography variant="h6">Ticket categories</Typography>
      {data && <Typography variant="caption" color="text.secondary">{data.ready} of {data.total} analyzed</Typography>}
      {!!data?.failed && <Button disabled={!data.enabled || retryLoading} onClick={retryFailed}>Retry failed analysis</Button>}
    </Stack>
    <Box sx={{ minHeight: 4 }}>
      {(loading || pending || !data) && <LinearProgress aria-label="Loading ticket categories" variant={pending ? 'determinate' : 'indeterminate'} value={data?.total ? data.ready / data.total * 100 : 0} />}
    </Box>
    {error && <Alert severity="warning">{error}</Alert>}
    {data?.message && <Alert severity={data.failed ? 'warning' : 'info'}>{data.message}</Alert>}
    <TicketCategoriesTable rows={data?.theo_categories || []} bandCounts={automationCounts} hasAutomation={!!automation}
      showSkeleton={!data || (pending && !data.ready)} disabled={loading} selectedCategory={selectedCategory}
      onCategoryDrill={row => onDrill('ai_theo', row.key, row.label, customerIds)}
      onSubcategoryDrill={(row, detail) => onDrill('ai_theo_subcategory', JSON.stringify([row.key, detail.key]), `${row.label} · ${detail.label}`, customerIds)}
      onBandDrill={(band, row) => onAutomationDrill?.(band, row.key, customerIds)} />
    {data && !pending && !data.theo_categories.length && <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>No categories assigned to these tickets.</Typography>}
  </Box>;
}
